import { useState, useEffect } from 'react';
import { Layout, Table, Button, Modal, Form, Input, message, Spin, Select } from 'antd';
import AdminSidebar from '../components/Sidebar';
import AppHeader from '../components/Header';
import { encryptData, decryptData } from '../middleware/encryption';

const { Content } = Layout;
const { TextArea } = Input;
const { Option } = Select;

export default function PricingPlan() {
  const [collapsed, setCollapsed] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form] = Form.useForm();
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const myHeaders = new Headers();
      const token = sessionStorage.getItem('access_token');
      if (token) {
        myHeaders.append("Authorization", `Bearer ${token}`);
      }

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow"
      };

      const response = await fetch("https://api.app.exellius.com/api/plans/getAllPlans", requestOptions);
      const result = await response.json();
      if (result.data) {
        const decryptedResponse = decryptData(result.data);
        setPlans(decryptedResponse.data);
      } else {
        message.error(result.message || "Failed to fetch plans");
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      message.error("An error occurred while fetching plans");
    } finally {
      setLoading(false);
    }
  };

  const showEditModal = (plan) => {
    setSelectedPlan(plan);
    form.setFieldsValue(plan);
    setIsModalVisible(true);
  };

  const handleEditCancel = () => {
    setIsModalVisible(false);
    setSelectedPlan(null);
    form.resetFields();
  };

  const handleEditOk = async () => {
    try {
      const values = await form.validateFields();
      const token = sessionStorage.getItem('access_token');
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      const features = Array.isArray(values.features) ? values.features : values.features.split(',');

      const requestOptions = {
        method: "PUT",
        headers: myHeaders,
        body: JSON.stringify({
          id: selectedPlan.id,
          ...values,
          features
        }),
        redirect: "follow"
      };

      setUpdating(true);
      const response = await fetch("https://api.app.exellius.com/api/plans/updatePlan", requestOptions);
      const result = await response.json();

      if (response.ok) {
        message.success("Plan updated successfully");
        setIsModalVisible(false);
        setSelectedPlan(null);
        form.resetFields();
        fetchPlans();
      } else {
        message.error(result.message || "Failed to update plan");
      }
    } catch (error) {
      console.error("Error updating plan:", error);
      message.error("An error occurred while updating the plan");
    } finally {
      setUpdating(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Points',
      dataIndex: 'points',
      key: 'points',
    },
    {
      title: 'Monthly Price',
      dataIndex: 'monthly_price',
      key: 'monthly_price',
    },
    {
      title: 'Annual Price',
      dataIndex: 'annual_price',
      key: 'annual_price',
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button type="link" onClick={() => showEditModal(record)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '125vh' }}>
      <AdminSidebar collapsed={collapsed} onCollapse={setCollapsed} />
      <Layout className="site-layout">
        <AppHeader />
        <Content style={{ margin: '0 16px' }}>
          {loading ? (
            <Spin size="large" style={{ display: 'block', margin: 'auto', marginTop: '20%' }} />
          ) : (
            <Table dataSource={plans} columns={columns} rowKey="id" pagination={false} />
          )}
          <Modal
            title="Edit Plan"
            visible={isModalVisible}
            onOk={handleEditOk}
            onCancel={handleEditCancel}
            okText="Save"
            confirmLoading={updating}
          >
            <Form form={form} layout="vertical">
              <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter the plan name' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Please enter the plan description' }]}>
                <TextArea rows={4} />
              </Form.Item>
              <Form.Item name="points" label="Points" rules={[{ required: true, message: 'Please enter the plan points' }]}>
                <Input type="number" />
              </Form.Item>
              <Form.Item name="monthly_price" label="Monthly Price" rules={[{ required: true, message: 'Please enter the monthly price' }]}>
                <Input type="number" />
              </Form.Item>
              <Form.Item name="annual_price" label="Annual Price" rules={[{ required: true, message: 'Please enter the annual price' }]}>
                <Input type="number" />
              </Form.Item>
              <Form.Item name="duration" label="Duration" rules={[{ required: true, message: 'Please enter the plan duration' }]}>
                <Input />
              </Form.Item>
              <Form.Item name="features" label="Features" rules={[{ required: true, message: 'Please enter the plan features' }]}>
                <TextArea rows={6} placeholder="Enter each feature on a new line" />
              </Form.Item>
            </Form>
          </Modal>
        </Content>
      </Layout>
    </Layout>
  );
}
